import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { getItemVisaoService } from '../services/visaoItem.service';
import {
  Informacao,
  Icons,
  Row,
  ButtonGoBack,
  Button,
  Tooltip,
  PageTitle,
} from '~/components';
import { iconEnum } from '~/modules/Helper/iconEnum';
import { ChooseIconFile } from '~/modules/Helper/chooseIconFile';
import { fileManagerPaiDownload } from '~/services/FileManager/file-manager.service';
import { formatarTamanhoBytes } from '../helpers/arquivos.helper';
import { obterDataHoraPartes } from '~/modules/Helper/DateTimeHelper';
import VisaoItemGrupo from '../components/visaoItemGrupo.component';
import {
  ArquivoIcone,
  ArquivoLegenda,
  ArquivoLinha,
  ArquivoNome,
  ArquivoTexto,
} from '../components/visaoItemGrupo.styled';

function formatarData(valor, comHora) {
  if (!valor) return '';
  const texto = String(valor);
  const partes = texto.match(/^(\d{4})-(\d{2})-(\d{2})(?:T(\d{2}):(\d{2}))?/);
  if (!partes) return obterDataHoraPartes(valor).data;

  if (!texto.includes('T')) {
    return `${partes[3]}/${partes[2]}/${partes[1]}`;
  }

  const data = new Date(texto);
  if (Number.isNaN(data.getTime())) {
    return `${partes[3]}/${partes[2]}/${partes[1]}`;
  }

  const dia = new Intl.DateTimeFormat('pt-BR', {
    timeZone: 'America/Sao_Paulo',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(data);

  if (!comHora) return dia;

  const hora = new Intl.DateTimeFormat('pt-BR', {
    timeZone: 'America/Sao_Paulo',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(data);

  return hora === '00:00' ? dia : `${dia} às ${hora}`;
}

function formatarTamanho(bytes) {
  if (bytes === 0) return '0 B';
  return formatarTamanhoBytes(bytes);
}

function formatarExtensao(extensao) {
  if (!extensao) return '';
  return String(extensao).replace(/^\./, '').toUpperCase();
}

function montarLegendaArquivo(arquivo) {
  const partes = [];
  const extensao = formatarExtensao(arquivo?.ExtensaoArquivo);

  if (extensao) partes.push(extensao);

  if (
    arquivo?.TamanhoBytesArquivo !== null &&
    arquivo?.TamanhoBytesArquivo !== undefined
  ) {
    partes.push(formatarTamanho(arquivo.TamanhoBytesArquivo));
  }

  const inclusao = formatarData(arquivo?.DataInclusaoArquivo, true);
  if (inclusao) partes.push(`incluído em ${inclusao}`);

  return partes.join(' · ');
}

function VisaoItem({ match }) {
  const history = useHistory();
  const [visaoItem, setVisaoItem] = useState(null);

  const { modulo, visao, itemVisao } = match.params;

  useEffect(() => {
    async function getItemVisao() {
      const response = await getItemVisaoService(modulo, visao, itemVisao);

      if (response?.data) setVisaoItem(response.data);
    }

    getItemVisao();
  }, [modulo, visao, itemVisao]);

  function goBack() {
    history.goBack();
  }

  const identificacao = visaoItem?.Identificacao;
  const arquivo = visaoItem?.ArquivoAnexo;
  const extensaoIcone = (
    arquivo?.ExtensaoArquivo ||
    arquivo?.NomeArquivo?.split('.').pop() ||
    ''
  )
    .replace(/^\./, '')
    .toLowerCase();
  const legendaArquivo = montarLegendaArquivo(arquivo);

  return (
    <>
      <PageTitle titulo={visaoItem?.Titulo || 'Item da visão'} />
      {visaoItem?.TextoInformativo && (
        <Informacao
          titulo="Informações"
          informacao={visaoItem.TextoInformativo}
        />
      )}
      <VisaoItemGrupo
        titulo="Identificação"
        emLinha
        campos={[
          { rotulo: 'Tipo', valor: identificacao?.TipoDocumento },
          { rotulo: 'Número', valor: identificacao?.NumeroDocumento },
          {
            rotulo: 'Data',
            valor: formatarData(identificacao?.DataDocumento),
          },
        ]}
      />
      {arquivo?.NomeArquivo && (
        <VisaoItemGrupo titulo="Arquivo">
          <Tooltip title="Baixar Arquivo" placement="topRight">
            <ArquivoLinha
              role="button"
              tabIndex={0}
              aria-label={
                legendaArquivo
                  ? `Baixar ${arquivo.NomeArquivo}. ${legendaArquivo}`
                  : `Baixar ${arquivo.NomeArquivo}`
              }
              onClick={() => fileManagerPaiDownload(arquivo.UrlArquivo)}
              onKeyDown={event => {
                if (
                  event.key === 'Enter' ||
                  event.key === ' ' ||
                  event.keyCode === 13
                ) {
                  event.preventDefault();
                  fileManagerPaiDownload(arquivo.UrlArquivo);
                }
              }}
            >
              <ArquivoIcone aria-hidden>
                {iconEnum
                  .find(x => x.name === `file-${ChooseIconFile(extensaoIcone)}`)
                  ?.value({ fontSize: '20px' })}
              </ArquivoIcone>
              <ArquivoTexto>
                <ArquivoNome>{arquivo.NomeArquivo}</ArquivoNome>
                {legendaArquivo ? (
                  <ArquivoLegenda>{legendaArquivo}</ArquivoLegenda>
                ) : null}
              </ArquivoTexto>
              <Button
                type="primary"
                shape="circle"
                icon={<Icons.DownloadOutlined />}
                aria-label={`Baixar arquivo ${arquivo.NomeArquivo}`}
                tabIndex={-1}
              />
            </ArquivoLinha>
          </Tooltip>
        </VisaoItemGrupo>
      )}
      <Row>
        <ButtonGoBack OnClick={goBack} />
      </Row>
    </>
  );
}

export default VisaoItem;
